import React, { useRef, useState, useEffect } from 'react';
import styles from './TimePicker.module.css';
import { getClassName } from '../../utils/className';
import Checkbox from '../Checkbox/Checkbox.jsx';

const pad = (n) => String(n).padStart(2, '0');

/**
 * Parses a 24-hour "HH:mm" string into { hour, minute }, or null if invalid.
 */
const parseTimeString = (str) => {
  if (!str || typeof str !== 'string') {
    return null;
  }
  const match = str.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return { hour, minute };
};

const formatTimeValue = (hour, minute) => `${pad(hour)}:${pad(minute)}`;

const formatDisplayTime = (parsed, is12Hours, placeholder) => {
  if (!parsed) {
    return placeholder;
  }
  const { hour, minute } = parsed;
  if (is12Hours) {
    const period = hour < 12 ? 'AM' : 'PM';
    let hour12 = hour % 12;
    if (hour12 === 0) {
      hour12 = 12;
    }
    return `${pad(hour12)}:${pad(minute)} ${period}`;
  }
  return `${pad(hour)}:${pad(minute)}`;
};

const to24Hour = (hour12, period) => {
  const h = hour12 % 12;
  return period === 'PM' ? h + 12 : h;
};

const findClosestIndex = (options, target) => {
  let closestIndex = 0;
  let minDiff = Infinity;
  options.forEach((option, index) => {
    const diff = Math.abs(option - target);
    if (diff < minDiff) {
      minDiff = diff;
      closestIndex = index;
    }
  });
  return closestIndex;
};

/**
 * A single scrollable column of options with CSS scroll-snap.
 * Supports mouse-wheel/touch scrolling as well as clicking an option directly.
 */
const PickerColumn = ({ options, selectedValue, onSelect, disabled, formatLabel, itemHeight, scrollSettleMs }) => {
  const containerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const selectedIndex = findClosestIndex(options, selectedValue);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const targetTop = selectedIndex * itemHeight;
    if (Math.abs(el.scrollTop - targetTop) > 1) {
      el.scrollTop = targetTop;
    }
  }, [selectedIndex, itemHeight]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleScroll = () => {
    if (disabled) {
      return;
    }
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const rawIndex = Math.round(el.scrollTop / itemHeight);
      const idx = Math.max(0, Math.min(options.length - 1, rawIndex));
      el.scrollTop = idx * itemHeight;
      if (options[idx] !== selectedValue) {
        onSelect(options[idx]);
      }
    }, scrollSettleMs);
  };

  const handleItemClick = (idx) => {
    if (disabled) {
      return;
    }
    const el = containerRef.current;
    if (el) {
      el.scrollTo({ top: idx * itemHeight, behavior: 'smooth' });
    }
    onSelect(options[idx]);
  };

  return (
    <div className={styles.pickerColumn} ref={containerRef} onScroll={handleScroll}>
      <div className={styles.pickerSpacer} />
      {options.map((option, idx) => (
        <div
          key={option}
          className={getClassName(styles.pickerItem, idx === selectedIndex && styles.pickerItemSelected)}
          onClick={() => handleItemClick(idx)}
        >
          {formatLabel(option)}
        </div>
      ))}
      <div className={styles.pickerSpacer} />
    </div>
  );
};

/**
 * A custom, self-contained TimePicker component.
 * Renders a label + value-pill row that expands an inline scroll-wheel picker (dark theme).
 *
 * @component
 * @param {Object} props
 * @param {string|null} [props.value] - The selected time as a 24-hour "HH:mm" string.
 * @param {(time: string) => void} [props.onChange] - Callback fired when the time changes. Receives a 24-hour "HH:mm" string.
 * @param {string} [props.label] - Optional label shown to the left of the value.
 * @param {boolean} [props.checkbox=false] - Render a checkbox before the label.
 * @param {boolean} [props.checked=false] - Checked state of the checkbox (when `checkbox` is enabled).
 * @param {(checked: boolean) => void} [props.onCheckedChange] - Callback fired when the checkbox is toggled.
 * @param {string} [props.checkboxColor] - Custom color for the checked checkbox.
 * @param {boolean} [props.disabled=false] - Disables interaction.
 * @param {boolean} [props.is12Hours=false] - Display hours in 12-hour format with an AM/PM column.
 * @param {number} [props.minuteStep=1] - Interval between selectable minutes.
 * @param {boolean} [props.open] - Controls open state externally. When provided, the component is controlled.
 * @param {(open: boolean) => void} [props.onOpenChange] - Callback fired when the open state should change.
 * @param {string} [props.placeholder='--:--'] - Text shown in the value pill when empty.
 * @param {number} [props.itemHeight=30] - Height in px of a single wheel row.
 * @param {number} [props.visibleCount=5] - Number of rows visible per column (should be odd).
 * @param {number} [props.scrollSettleMs=120] - Delay in ms after scrolling stops before snapping to a value.
 * @param {string} [props.className] - Class name for the outer wrapper.
 * @param {React.CSSProperties} [props.style] - Styles for the outer wrapper.
 * @param {string} [props.valuePillClassName] - Class name for the value-pill button that opens/closes the picker.
 * @param {React.CSSProperties} [props.valuePillStyle] - Styles for the value-pill button that opens/closes the picker.
 */
const TimePicker = ({
  value,
  onChange,
  label,
  checkbox = false,
  checked = false,
  onCheckedChange,
  checkboxColor,
  disabled = false,
  is12Hours = false,
  minuteStep = 1,
  open,
  onOpenChange,
  placeholder = '--:--',
  itemHeight = 30,
  visibleCount = 5,
  scrollSettleMs = 120,
  className,
  style,
  valuePillClassName,
  valuePillStyle,
  ...rest
}) => {
  const paddingCount = Math.floor(visibleCount / 2);
  const containerRef = useRef(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const setOpen = (next) => {
    if (!isControlled) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  };

  const parsedValue = parseTimeString(value);
  const hour24 = parsedValue ? parsedValue.hour : 0;
  const minute = parsedValue ? parsedValue.minute : 0;
  const period = hour24 < 12 ? 'AM' : 'PM';
  let hour12 = hour24 % 12;
  if (hour12 === 0) {
    hour12 = 12;
  }

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) {
      return;
    }
    setOpen(!isOpen);
  };

  const handleCheckedChange = () => {
    if (disabled) {
      return;
    }
    onCheckedChange?.(!checked);
  };

  const emitChange = (nextHour24, nextMinute) => {
    onChange?.(formatTimeValue(nextHour24, nextMinute));
  };

  const handleHourSelect = (nextHour) => {
    const nextHour24 = is12Hours ? to24Hour(nextHour, period) : nextHour;
    emitChange(nextHour24, minute);
  };

  const handleMinuteSelect = (nextMinute) => {
    emitChange(hour24, nextMinute);
  };

  const handlePeriodSelect = (nextPeriodIndex) => {
    const nextPeriod = nextPeriodIndex === 0 ? 'AM' : 'PM';
    emitChange(to24Hour(hour12, nextPeriod), minute);
  };

  const hourOptions = is12Hours
    ? Array.from({ length: 12 }, (_, i) => i + 1)
    : Array.from({ length: 24 }, (_, i) => i);
  const minuteOptions = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep).filter(
    (m) => m < 60
  );

  const wheelVars = {
    '--tp-item-height': `${itemHeight}px`,
    '--tp-visible-count': visibleCount,
    '--tp-padding-count': paddingCount,
  };
  if (checkboxColor) {
    wheelVars['--tp-checkbox-color'] = checkboxColor;
  }

  return (
    <div
      ref={containerRef}
      className={getClassName(styles.timePicker, className)}
      style={{ ...wheelVars, ...style }}
      {...rest}
    >
      <div className={styles.topRow}>
        {(checkbox || label != null) && (
          <div className={styles.labelGroup}>
            {checkbox && (
              <Checkbox
                checked={checked}
                onChange={handleCheckedChange}
                disabled={disabled}
                size="small"
                className={getClassName(checked && checkboxColor && styles.checkboxColored)}
              />
            )}
            {label != null && <span className={styles.label}>{label}</span>}
          </div>
        )}
        <div
          className={getClassName(styles.valuePill, disabled && styles.valuePillDisabled, valuePillClassName)}
          style={valuePillStyle}
          onClick={handleToggle}
        >
          {formatDisplayTime(parsedValue, is12Hours, placeholder)}
        </div>
      </div>
      {isOpen && (
        <div className={styles.pickerWrapper}>
          <div className={styles.pickerHighlight} />
          <div className={styles.pickerRow}>
            <PickerColumn
              options={hourOptions}
              selectedValue={is12Hours ? hour12 : hour24}
              onSelect={handleHourSelect}
              disabled={disabled}
              formatLabel={pad}
              itemHeight={itemHeight}
              scrollSettleMs={scrollSettleMs}
            />
            <PickerColumn
              options={minuteOptions}
              selectedValue={minute}
              onSelect={handleMinuteSelect}
              disabled={disabled}
              formatLabel={pad}
              itemHeight={itemHeight}
              scrollSettleMs={scrollSettleMs}
            />
            {is12Hours && (
              <PickerColumn
                options={[0, 1]}
                selectedValue={period === 'AM' ? 0 : 1}
                onSelect={handlePeriodSelect}
                disabled={disabled}
                formatLabel={(i) => (i === 0 ? 'AM' : 'PM')}
                itemHeight={itemHeight}
                scrollSettleMs={scrollSettleMs}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
